import {
  AIR_PASS_HEIGHT,
  BANK,
  COURSE_WIDTH,
  PLAYER_DRAW,
  SNOW_TILE,
  TRACK_LENGTH,
} from "./constants";
import type { AssetMap } from "./assets";
import { getObject } from "./objects";
import type { GameSim } from "./sim";
import { computeView } from "./viewCamera";
import { createGlView, type GameView } from "./gl/GlView";

export type { GameView };

function noise1(n: number): number {
  const x = Math.sin(n * 127.1) * 43758.5453;
  return x - Math.floor(x);
}

export function resizeCanvas(canvas: HTMLCanvasElement): { w: number; h: number; dpr: number } {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(1, canvas.clientWidth);
  const h = Math.max(1, canvas.clientHeight);
  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) {
    canvas.width = pw;
    canvas.height = ph;
  }
  return { w, h, dpr };
}

export function renderWorld(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  assets: AssetMap,
  viewW: number,
  viewH: number,
  alpha: number,
  dpr: number,
): void {
  void alpha;
  const p = sim.player;
  const { scale, frac, camX, camY, viewWorldH, ox, oy } = computeView(sim, viewW, viewH);

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, viewW, viewH);
  ctx.save();
  ctx.translate(viewW / 2 + ox, viewH * frac + oy);
  ctx.rotate(sim.camRoll);
  ctx.translate(-viewW / 2, -viewH * frac);

  const toScreen = (x: number, y: number) => ({
    sx: (x - camX) * scale + viewW / 2,
    sy: (y - camY) * scale,
  });

  drawBanks(ctx, sim, assets, toScreen, scale, viewW, viewH, camY, viewWorldH);
  drawSnow(ctx, assets, toScreen, scale, camY, viewWorldH);
  drawTracks(ctx, sim, toScreen, scale);
  drawFinish(ctx, sim, toScreen, scale);

  type DrawItem = { y: number; z: number; draw: () => void };
  const items: DrawItem[] = [];

  const visTop = camY - 80;
  const visBot = camY + viewWorldH + 140;

  for (const s of sim.scenery) {
    if (s.y < visTop || s.y > visBot) continue;
    items.push({
      y: s.y,
      z: 6,
      draw: () =>
        drawSprite(
          ctx,
          assets[s.sprite],
          toScreen,
          s.x,
          s.y,
          100 * s.scale,
          100 * s.scale,
          s.flip,
          0,
          1,
          scale,
        ),
    });
  }

  for (const e of sim.entities) {
    if (e.y < visTop || e.y > visBot) continue;
    const def = getObject(e.defId);
    // An eaten pickup is gone, not dimmed -- unlike a used ramp, which stays on
    // the hill as a landmark.
    if (e.consumed && def.collision === "pickup") continue;
    const sprite = def.sprites[e.variant % def.sprites.length]!;
    const img = assets[sprite];
    const fade = e.consumed && def.collision === "launch" ? 0.45 : 1;
    items.push({
      y: e.y,
      z: def.zBias,
      draw: () =>
        drawSprite(ctx, img, toScreen, e.x, e.y, def.size.w, def.size.h, false, 0, fade, scale),
    });
  }

  items.push({
    y: p.y,
    z: 4,
    draw: () => drawPlayer(ctx, sim, assets, toScreen, scale),
  });

  items.sort((a, b) => a.y - b.y || a.z - b.z);
  for (const it of items) it.draw();

  drawParticles(ctx, sim, toScreen, scale);
  ctx.restore();

  if (!sim.reducedMotion) drawSpeedLines(ctx, sim, viewW, viewH);
  drawSnowfall(ctx, sim, viewW, viewH);
  drawVignette(ctx, viewW, viewH, sim);
}

function drawSprite(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | undefined,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  x: number,
  y: number,
  w: number,
  h: number,
  flip: boolean,
  rot: number,
  alpha: number,
  scale: number,
): void {
  const { sx, sy } = toScreen(x, y);
  let dw = w * scale;
  let dh = h * scale;
  // Contained at the image's own aspect, as the WebGL view does: the box is a
  // bound, and a tall tree must not be squashed into a square.
  if (img && img.complete && img.naturalWidth > 0) {
    const a = img.naturalHeight / img.naturalWidth;
    dw = Math.min(dw, dh / a);
    dh = dw * a;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(sx, sy);
  if (flip) ctx.scale(-1, 1);
  if (rot) ctx.rotate(rot);
  if (img && img.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
  } else {
    ctx.fillStyle = "#3d342c";
    ctx.beginPath();
    ctx.ellipse(0, 0, dw * 0.35, dh * 0.35, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawSnow(
  ctx: CanvasRenderingContext2D,
  assets: AssetMap,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
  camY: number,
  viewWorldH: number,
): void {
  const top = toScreen(0, camY - 40);
  const bot = toScreen(COURSE_WIDTH, camY + viewWorldH + 80);
  ctx.fillStyle = "#e7eef2";
  ctx.fillRect(top.sx, top.sy, bot.sx - top.sx, bot.sy - top.sy);
  const img = assets.snow;
  const tile = SNOW_TILE * 0.85;
  const startY = Math.floor((camY - 40) / tile) * tile;
  const endY = camY + viewWorldH + 80;
  ctx.imageSmoothingEnabled = true;
  ctx.globalAlpha = 0.32;
  if (img && img.complete && img.naturalWidth > 0) {
    for (let y = startY; y < endY; y += tile) {
      for (let x = 0; x < COURSE_WIDTH; x += tile) {
        const { sx, sy } = toScreen(x, y);
        ctx.drawImage(img, sx, sy, tile * scale + 1, tile * scale + 1);
      }
    }
  }
  ctx.globalAlpha = 1;
  const g = ctx.createLinearGradient(top.sx, 0, bot.sx, 0);
  g.addColorStop(0, "rgba(28,46,36,0.22)");
  g.addColorStop(0.1, "rgba(28,46,36,0)");
  g.addColorStop(0.9, "rgba(28,46,36,0)");
  g.addColorStop(1, "rgba(28,46,36,0.22)");
  ctx.fillStyle = g;
  ctx.fillRect(top.sx, top.sy, bot.sx - top.sx, bot.sy - top.sy);
}

function drawBanks(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  assets: AssetMap,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
  viewW: number,
  viewH: number,
  camY: number,
  viewWorldH: number,
): void {
  void assets;
  void scale;
  void sim;
  ctx.fillStyle = "#15241c";
  ctx.fillRect(0, 0, viewW, viewH);
  const left = toScreen(-BANK, camY - 40);
  const right = toScreen(COURSE_WIDTH, camY - 40);
  const h = (viewWorldH + 80) * (viewH / viewWorldH);
  ctx.fillStyle = "#1a2c22";
  ctx.fillRect(left.sx, left.sy, toScreen(0, camY).sx - left.sx, h);
  ctx.fillRect(right.sx, right.sy, toScreen(COURSE_WIDTH + BANK, camY).sx - right.sx, h);
}

function drawTracks(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
): void {
  if (sim.tracks.length < 2) return;
  ctx.strokeStyle = "rgba(125, 147, 160, 0.28)";
  ctx.lineWidth = 7 * scale;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  sim.tracks.forEach((t, i) => {
    const { sx, sy } = toScreen(t.x - 7, t.y);
    if (i === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  });
  ctx.stroke();
  ctx.beginPath();
  sim.tracks.forEach((t, i) => {
    const { sx, sy } = toScreen(t.x + 7, t.y);
    if (i === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  });
  ctx.stroke();
}

function drawFinish(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
): void {
  void sim;
  const y = TRACK_LENGTH;
  const a = toScreen(18, y);
  const b = toScreen(COURSE_WIDTH - 18, y);
  ctx.strokeStyle = "#3d342c";
  ctx.lineWidth = 6 * scale;
  ctx.beginPath();
  ctx.moveTo(a.sx, a.sy - 36 * scale);
  ctx.lineTo(a.sx, a.sy + 10 * scale);
  ctx.moveTo(b.sx, b.sy - 36 * scale);
  ctx.lineTo(b.sx, b.sy + 10 * scale);
  ctx.stroke();
  const bannerH = 26 * scale;
  ctx.fillStyle = "#f4f1ea";
  ctx.beginPath();
  ctx.rect(a.sx, a.sy - 40 * scale, b.sx - a.sx, bannerH);
  ctx.fill();
  ctx.fillStyle = "#1c2e24";
  ctx.font = `600 ${18 * scale}px Fraunces, serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("FINISH", (a.sx + b.sx) / 2, a.sy - 40 * scale + bannerH / 2);
}

function drawPlayer(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  assets: AssetMap,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
): void {
  const p = sim.player;
  const { sx, sy } = toScreen(p.x, p.y);
  const air = p.height;
  const lift = air * 0.55 * scale;
  const size = PLAYER_DRAW * (1 + air * 0.0045) * scale;
  const shadow = 0.35 + Math.min(0.45, air / 180);
  ctx.save();
  ctx.translate(sx, sy);
  ctx.fillStyle = `rgba(12,18,16,${0.28 * shadow})`;
  ctx.beginPath();
  ctx.ellipse(
    0,
    10 * scale,
    (size * 0.42) / (1 + air * 0.01),
    (size * 0.18) / (1 + air * 0.012),
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();
  ctx.translate(0, -lift);
  ctx.rotate(p.lean * 0.32);
  ctx.scale(p.squash, 2 - p.squash);
  if (p.stun > 0 && Math.floor(sim.clock * 18) % 2 === 0) ctx.globalAlpha = 0.55;
  const frame = Math.floor(p.frame) % 4;
  const img = assets[`player-${frame}`];
  if (img && img.complete && img.naturalWidth > 0)
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
  else {
    ctx.fillStyle = "#c45c4a";
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.38, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  if (p.height > AIR_PASS_HEIGHT) {
    ctx.save();
    ctx.strokeStyle = "rgba(244,241,234,0.35)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(sx, sy - lift, size * 0.48, size * 0.2, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

function drawParticles(
  ctx: CanvasRenderingContext2D,
  sim: GameSim,
  toScreen: (x: number, y: number) => { sx: number; sy: number },
  scale: number,
): void {
  for (const q of sim.particles) {
    const { sx, sy } = toScreen(q.x, q.y);
    const a = Math.max(0, q.max > 0 ? q.life / q.max : 0);
    ctx.fillStyle =
      q.kind === "burst" ? `rgba(244,241,234,${0.8 * a})` : `rgba(232,238,242,${0.7 * a})`;
    ctx.beginPath();
    ctx.ellipse(sx, sy, q.size * scale, q.size * scale, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSpeedLines(ctx: CanvasRenderingContext2D, sim: GameSim, w: number, h: number): void {
  const t = Math.max(0, (sim.player.speedY - 420) / 560);
  if (t <= 0) return;
  ctx.save();
  ctx.globalAlpha = 0.18 * t;
  ctx.strokeStyle = "#f4f1ea";
  ctx.lineWidth = 1.5;
  const n = Math.floor(10 + t * 16);
  for (let i = 0; i < n; i++) {
    const seed = i * 17.17 + Math.floor(sim.clock * (40 + t * 80));
    const x = noise1(seed) * w;
    const y0 = noise1(seed + 3) * h;
    const len = 18 + t * 70;
    ctx.beginPath();
    ctx.moveTo(x, y0);
    ctx.lineTo(x, y0 + len);
    ctx.stroke();
  }
  ctx.restore();
}

function drawSnowfall(ctx: CanvasRenderingContext2D, sim: GameSim, w: number, h: number): void {
  ctx.fillStyle = "rgba(244,241,234,0.55)";
  const n = sim.reducedMotion ? 12 : 38;
  const speed = 40 + sim.player.speedY * 0.12;
  const spanX = w + 20;
  const spanY = h + 20;
  for (let i = 0; i < n; i++) {
    const px = ((((noise1(i * 19.1) * w + sim.clock * 18) % spanX) + spanX) % spanX) - 10;
    const py = ((((noise1(i * 7.7) * h + sim.clock * speed) % spanY) + spanY) % spanY) - 10;
    ctx.beginPath();
    ctx.arc(px, py, i % 5 === 0 ? 2.2 : 1.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawVignette(ctx: CanvasRenderingContext2D, w: number, h: number, sim: GameSim): void {
  const g = ctx.createRadialGradient(w / 2, h * 0.35, h * 0.2, w / 2, h * 0.4, h * 0.85);
  g.addColorStop(0, "rgba(12,18,16,0)");
  g.addColorStop(1, "rgba(12,18,16,0.42)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  if (sim.player.stun > 0) {
    ctx.fillStyle = `rgba(61,52,44,${0.18 * Math.min(1, sim.player.stun * 3)})`;
    ctx.fillRect(0, 0, w, h);
  }
}

function createCanvasView(canvas: HTMLCanvasElement, assets: AssetMap): GameView {
  const ctx = canvas.getContext("2d");
  return {
    render(sim, alpha) {
      if (!ctx) return;
      const { w, h, dpr } = resizeCanvas(canvas);
      renderWorld(ctx, sim, assets, w, h, alpha, dpr);
    },
    dispose() {},
  };
}

export function createView(canvas: HTMLCanvasElement, assets: AssetMap): GameView {
  try {
    return createGlView(canvas, assets);
  } catch {
    return createCanvasView(canvas, assets);
  }
}
